// src/pages/Services/Register.jsx

import React, { useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from '../../axios';
import {
  Box, Container, Paper, Avatar, Typography, TextField, Button, Alert, Link,
  Fade, InputAdornment, IconButton, CircularProgress, Grid, Divider, LinearProgress,
} from '@mui/material';
import {
  AppRegistration, BusinessOutlined, PhoneAndroidOutlined, EmailOutlined,
  PersonOutline, LockOutlined, Visibility, VisibilityOff, CheckCircle,
  RadioButtonUnchecked, ErrorOutline, TaskAlt, RocketLaunchOutlined,
} from '@mui/icons-material';

// ───────────────────────── تصميم مشترك مع صفحة الدخول ─────────────────────────
const pageSx = {
  position: 'fixed', inset: 0, zIndex: 9999, overflowY: 'auto',
  display: 'flex', justifyContent: 'center', alignItems: { xs: 'flex-start', md: 'center' },
  py: { xs: 3, md: 4 }, px: 2,
  background: 'linear-gradient(135deg, #0f172a 0%, #1e1b4b 40%, #2e1065 100%)',
  '&::before': {
    content: '""', position: 'fixed', inset: 0, zIndex: 0, pointerEvents: 'none',
    backgroundImage: `
      radial-gradient(circle at 20% 20%, rgba(99,102,241,.25) 0%, transparent 50%),
      radial-gradient(circle at 80% 80%, rgba(16,185,129,.15) 0%, transparent 50%)`,
  },
};

const cardSx = {
  position: 'relative', overflow: 'hidden', width: '100%',
  p: { xs: 3, sm: 5 }, borderRadius: '28px',
  display: 'flex', flexDirection: 'column', alignItems: 'center',
  background: 'rgba(255,255,255,.98)',
  boxShadow: '0 40px 80px -20px rgba(0,0,0,.6)',
};

const topBarSx = {
  position: 'absolute', top: 0, left: 0, height: 6, width: '100%',
  background: 'linear-gradient(90deg, #4F46E5 0%, #10B981 100%)',
};

const inputSx = {
  '& .MuiOutlinedInput-root': { borderRadius: '14px', bgcolor: '#f8fafc' },
};

const primaryBtnSx = {
  py: 1.6, fontSize: '1rem', fontWeight: 700, borderRadius: '14px',
  background: 'linear-gradient(90deg, #4F46E5 0%, #6366F1 100%)',
  boxShadow: '0 10px 25px -6px rgba(79,70,229,.4)',
  '&:hover': { background: 'linear-gradient(90deg, #3730A3 0%, #4F46E5 100%)' },
};

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const MOBILE_REGEX = /^05\d{8}$/;

const emptyForm = { companyName: '', Mobile: '', Email: '', userName: '', password: '' };

// تحويل +9665xxxxxxxx أو 9665xxxxxxxx أو 5xxxxxxxx إلى 05xxxxxxxx
const normalizeMobile = (v) => {
  let p = v.replace(/[^\d+]/g, '');
  if (p.startsWith('+966')) p = '0' + p.slice(4);
  else if (p.startsWith('966')) p = '0' + p.slice(3);
  if (p.startsWith('5') && p.length === 9) p = '0' + p;
  return p.slice(0, 10);
};

// استخراج رسالة مقروءة من أي شكل لرد الخطأ
const extractError = (error, fallback) => {
  const d = error.response?.data;
  if (!d) return error.message || fallback;
  if (typeof d === 'string') return d;
  if (d.message) return d.message;
  if (d.errors) {
    const first = Object.values(d.errors).flat()[0];
    if (first) return first;
  }
  return d.title || fallback;
};

const SectionLabel = ({ children }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%', mb: 2, mt: 0.5 }}>
    <Typography variant="caption" sx={{ fontWeight: 800, color: '#4F46E5', letterSpacing: 0.5, whiteSpace: 'nowrap' }}>
      {children}
    </Typography>
    <Divider sx={{ flex: 1 }} />
  </Box>
);

export default function Register() {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const navigate = useNavigate();

  const [form, setForm] = useState(emptyForm);
  const [touched, setTouched] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);
  const [mobileStatus, setMobileStatus] = useState(null); // null | 'checking' | 'available' | 'taken'

  // ── التحقق ──
  const rules = [
    { ok: form.password.length >= 8, label: t('auth.rule_length', '8 أحرف على الأقل') },
    { ok: /[A-Za-z\u0600-\u06FF]/.test(form.password), label: t('auth.rule_letters', 'حروف') },
    { ok: /\d/.test(form.password), label: t('auth.rule_digits', 'أرقام') },
  ];
  const score = rules.filter(r => r.ok).length + (form.password.length >= 12 ? 1 : 0);
  const strengthColor = ['#e2e8f0', '#ef4444', '#f59e0b', '#10b981', '#059669'][score];

  const errors = {
    companyName: !form.companyName.trim() && t('register.required', 'مطلوب'),
    Mobile: !MOBILE_REGEX.test(form.Mobile)
      ? t('register.invalid_mobile', 'يجب أن يبدأ بـ 05 ويتكون من 10 أرقام')
      : mobileStatus === 'taken' && t('register.mobile_taken', 'رقم الجوال مسجل مسبقًا'),
    Email: !EMAIL_REGEX.test(form.Email.trim()) && t('auth.invalid_email', 'أدخل بريدًا إلكترونيًا صحيحًا'),
    userName: form.userName.trim().length < 3 && t('register.invalid_username', '3 أحرف على الأقل'),
    password: !rules.every(r => r.ok) && t('auth.password_rules_failed', 'كلمة المرور لا تستوفي الشروط'),
  };
  const showErr = (name) => Boolean(touched[name] && errors[name]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'Mobile') setMobileStatus(null);
    setForm(prev => ({ ...prev, [name]: name === 'Mobile' ? normalizeMobile(value) : value }));
  };

  const handleBlur = (e) => {
    const { name } = e.target;
    setTouched(prev => ({ ...prev, [name]: true }));
    if (name === 'Mobile') checkMobile();
  };

  // التحقق من توفر رقم الجوال
  const checkMobile = async () => {
    if (!MOBILE_REGEX.test(form.Mobile)) return;
    setMobileStatus('checking');
    try {
      const { data } = await axios.get(`/User/check-phone/${form.Mobile}`);
      setMobileStatus(data?.available ? 'available' : 'taken');
    } catch {
      setMobileStatus(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setTouched(Object.keys(emptyForm).reduce((a, k) => ({ ...a, [k]: true }), {}));
    if (Object.values(errors).some(Boolean)) return;

    setLoading(true);
    try {
      await axios.post('/auth/register-company', {
        ...form,
        companyName: form.companyName.trim(),
        Email: form.Email.trim(),
        userName: form.userName.trim(),
      });
      setDone(true);
      setTimeout(() => navigate('/login', { replace: true }), 3000);
    } catch (err) {
      setError(extractError(err, t('register.failed', 'تعذّر إنشاء الحساب، حاول مرة أخرى')));
    } finally {
      setLoading(false);
    }
  };

  const fieldProps = (name, icon, extra = {}) => ({
    name,
    fullWidth: true,
    value: form[name],
    onChange: handleChange,
    onBlur: handleBlur,
    error: showErr(name),
    helperText: showErr(name) ? errors[name] : extra.helperText,
    sx: inputSx,
    ...extra,
    InputProps: {
      startAdornment: (
        <InputAdornment position="start">
          {React.cloneElement(icon, { sx: { color: 'text.secondary', fontSize: '1.2rem' } })}
        </InputAdornment>
      ),
      ...extra.InputProps,
    },
  });

  const mobileAdornment = mobileStatus === 'checking'
    ? <CircularProgress size={16} />
    : mobileStatus === 'available'
      ? <CheckCircle sx={{ color: '#10b981', fontSize: 20 }} />
      : mobileStatus === 'taken'
        ? <ErrorOutline sx={{ color: '#ef4444', fontSize: 20 }} />
        : null;

  return (
    <Box sx={pageSx} dir={isRTL ? 'rtl' : 'ltr'}>
      <Fade in timeout={800}>
        <Container maxWidth="sm" sx={{ position: 'relative', zIndex: 1, px: { xs: 0, sm: 2 } }}>
          <Paper elevation={0} sx={cardSx}>
            <Box sx={topBarSx} />

            {done ? (
              // ── تم الإنشاء ──
              <Fade in>
                <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', py: 2 }}>
                  <Box sx={{
                    mt: 1, mb: 2.5, width: 88, height: 88, borderRadius: '50%', bgcolor: 'rgba(16,185,129,.1)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    <TaskAlt sx={{ fontSize: 46, color: '#10B981' }} />
                  </Box>
                  <Typography variant="h5" sx={{ fontWeight: 800, color: '#0F172A', mb: 1 }}>
                    {t('register.success_title', 'تم إنشاء حسابك بنجاح')}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500, mb: 3, lineHeight: 1.8 }}>
                    {t('register.success_hint', 'تم إنشاء المنشأة وحساب مدير النظام. سجّل الدخول برقم الجوال وكلمة المرور.')}
                  </Typography>
                  <LinearProgress sx={{ width: '60%', borderRadius: 2, mb: 3 }} />
                  <Button fullWidth variant="contained" component={RouterLink} to="/login" replace sx={primaryBtnSx}>
                    {t('auth.login_now', 'تسجيل الدخول الآن')}
                  </Button>
                </Box>
              </Fade>
            ) : (
              <>
                <Avatar sx={{
                  mt: 1, mb: 2, width: 60, height: 60,
                  background: 'linear-gradient(135deg, #4F46E5 0%, #3730A3 100%)',
                  boxShadow: '0 12px 24px -4px rgba(79,70,229,.4)',
                }}>
                  <AppRegistration />
                </Avatar>

                <Typography variant="h5" sx={{ fontWeight: 800, color: '#0F172A', mb: 0.75, textAlign: 'center' }}>
                  {t('register.title', 'تسجيل منشأة جديدة')}
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500, mb: 3.5, textAlign: 'center', lineHeight: 1.8, maxWidth: 400 }}>
                  {t('register.subtitle', 'جهّز بيئة العمل لمنشأتك وابدأ إصدار الفواتير الإلكترونية المتوافقة مع هيئة الزكاة')}
                </Typography>

                {error && (
                  <Alert severity="error" variant="filled" onClose={() => setError('')}
                    sx={{ width: '100%', mb: 3, borderRadius: '12px', fontWeight: 600 }}>
                    {error}
                  </Alert>
                )}

                <Box component="form" onSubmit={handleSubmit} noValidate sx={{ width: '100%' }}>
                  {/* ── بيانات المنشأة ── */}
                  <SectionLabel>{t('register.company_section', 'بيانات المنشأة')}</SectionLabel>
                  <Grid container spacing={2} sx={{ mb: 3 }}>
                    <Grid item xs={12}>
                      <TextField
                        label={t('register.company_name', 'اسم المنشأة')}
                        autoFocus
                        {...fieldProps('companyName', <BusinessOutlined />)}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        label={t('register.company_email', 'البريد الإلكتروني')}
                        type="email"
                        placeholder="name@company.com"
                        inputProps={{ dir: 'ltr' }}
                        {...fieldProps('Email', <EmailOutlined />, {
                          helperText: t('register.email_hint', 'يُستخدم لاستعادة كلمة المرور'),
                        })}
                      />
                    </Grid>
                  </Grid>

                  {/* ── حساب المدير ── */}
                  <SectionLabel>{t('register.admin_section', 'حساب مدير النظام')}</SectionLabel>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label={t('register.admin_name', 'اسم المستخدم')}
                        {...fieldProps('userName', <PersonOutline />)}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        label={t('register.mobile', 'رقم الجوال')}
                        placeholder="05XXXXXXXX"
                        inputProps={{ dir: 'ltr', inputMode: 'tel' }}
                        {...fieldProps('Mobile', <PhoneAndroidOutlined />, {
                          helperText: mobileStatus === 'available' && !showErr('Mobile')
                            ? t('register.mobile_available', 'الرقم متاح')
                            : t('register.mobile_hint', 'تسجّل الدخول به'),
                          InputProps: mobileAdornment
                            ? { endAdornment: <InputAdornment position="end">{mobileAdornment}</InputAdornment> }
                            : {},
                        })}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        label={t('auth.password', 'كلمة المرور')}
                        type={showPassword ? 'text' : 'password'}
                        inputProps={{ dir: 'ltr' }}
                        {...fieldProps('password', <LockOutlined />, {
                          helperText: undefined,
                          InputProps: {
                            endAdornment: (
                              <InputAdornment position="end">
                                <IconButton size="small" edge="end" tabIndex={-1} onClick={() => setShowPassword(s => !s)}>
                                  {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                                </IconButton>
                              </InputAdornment>
                            ),
                          },
                        })}
                      />

                      {/* مؤشر القوة */}
                      <Box sx={{ display: 'flex', gap: 0.75, mt: 1.25 }}>
                        {[1, 2, 3, 4].map(i => (
                          <Box key={i} sx={{
                            flex: 1, height: 5, borderRadius: 3, transition: 'background-color .3s',
                            bgcolor: i <= score ? strengthColor : '#e2e8f0',
                          }} />
                        ))}
                      </Box>
                      <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mt: 1 }}>
                        {rules.map(r => (
                          <Box key={r.label} sx={{
                            display: 'flex', alignItems: 'center', gap: 0.5, transition: 'color .3s',
                            color: r.ok ? '#10b981' : showErr('password') ? '#ef4444' : 'text.disabled',
                          }}>
                            {r.ok ? <CheckCircle sx={{ fontSize: 15 }} /> : <RadioButtonUnchecked sx={{ fontSize: 15 }} />}
                            <Typography variant="caption" fontWeight={600}>{r.label}</Typography>
                          </Box>
                        ))}
                      </Box>
                    </Grid>
                  </Grid>

                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    disabled={loading || mobileStatus === 'checking'}
                    endIcon={!loading && <RocketLaunchOutlined sx={{ transform: isRTL ? 'scaleX(-1)' : 'none' }} />}
                    sx={{ ...primaryBtnSx, mt: 4, mb: 3 }}
                  >
                    {loading
                      ? <CircularProgress size={24} color="inherit" />
                      : t('register.submit', 'إنشاء الحساب وبدء الفترة التجريبية')}
                  </Button>

                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600, textAlign: 'center' }}>
                    {t('register.have_account', 'لديك منشأة مسجلة بالفعل؟')}{' '}
                    <Link
                      component={RouterLink}
                      to="/login"
                      sx={{ fontWeight: 800, textDecoration: 'none', color: '#4F46E5', '&:hover': { textDecoration: 'underline' } }}
                    >
                      {t('register.login_link', 'تسجيل الدخول')}
                    </Link>
                  </Typography>
                </Box>
              </>
            )}
          </Paper>
        </Container>
      </Fade>
    </Box>
  );
}